import React, { useEffect, useState } from 'react';
import axios from 'axios';
import OperationTypeForm from './OperationTypeForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { SystemPill, ActiveStatusPill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

function OperationTypeTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/inventory-operation-types', { params: { status, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Operation Type "${item.code}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/inventory-operation-types/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/inventory-operation-types/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/inventory-operation-types/${editItem.id}`, data);
      } else {
        await axios.post('/api/inventory-operation-types', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Operation Types</h2>
        {canCreate && (
          <button
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            Add Operation Type
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <OperationTypeForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: '23%' }} />
            <col style={{ width: '36%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.code}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.name}</td>
                <td
                  style={{ padding: '6px 8px', fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={item.description || ''}
                >
                  {item.description || '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  {item.is_system_default ? <SystemPill /> : <ActiveStatusPill active={item.is_active} />}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      item.is_active
                        ? {
                            key: 'deactivate',
                            label: 'Deactivate',
                            danger: true,
                            disabled: showForm || item.is_system_default,
                            disabledTitle: showForm ? 'Finish editing first' : 'This is a locked Operation Type and cannot be deactivated',
                            onClick: () => handleDeactivate(item),
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            disabled: showForm,
                            disabledTitle: showForm ? 'Finish editing first' : undefined,
                            onClick: () => handleReactivate(item),
                          },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Operation Types"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default OperationTypeTable;
